import { makeStyles, Theme, Tooltip, Typography } from "@material-ui/core"
import MuiLink from "@material-ui/core/Link"
import { Link } from "react-router-dom"
import { Folder } from "react-feather"

import BoardCardMenu from "./BoardCardMenu"
import { useUrlWithContext } from "../../../hooks/use-url-with-context"
import { BoardViewModelV2 } from "../../api/v2/board-view-model"
import Crown from "../../../assets/icons/Crown"

type CardViewModel = BoardViewModelV2["columnsById"][string]["cards"][number]

type CardHeaderProps = {
  board: BoardViewModelV2
  card: CardViewModel
}

const TRUNCATE_PROJECT_TITLE_LENGTH = 20
const getTooltip = (title = "") => (title.length > TRUNCATE_PROJECT_TITLE_LENGTH ? title : "")

export function CardHeader({ card, board }: CardHeaderProps) {
  const classes = useStyles()

  const hasCustomers = Boolean(card.customers?.length)
  const hasProject = Boolean(card.projectLink) && !board.isProjectBoard && !board.isTaskBoard
  const showTwoRows = hasCustomers && hasProject

  return (
    <div className={classes.header}>
      <div className={classes.headerRow}>
        <div className={classes.rowContent}>
          {hasCustomers ? (
            <CustomerInfo customers={card.customers} />
          ) : hasProject ? (
            <ProjectLink link={card.projectLink!} />
          ) : null}
        </div>

        <div className={classes.rowAction}>
          <BoardCardMenu board={board} card={card} />
        </div>
      </div>

      {showTwoRows && (
        <div className={classes.headerRow}>
          <div className={classes.rowContent}>
            <ProjectLink link={card.projectLink!} />
          </div>

          <div className={classes.rowAction} />
        </div>
      )}
    </div>
  )
}

function CustomerInfo({ customers }: { customers: CardViewModel["customers"] }) {
  if (!customers?.length) return null

  const customerNames = customers.map((c) => c.name).join(", ")

  return (
    <>
      <span className={"iconWrapper"}>
        <Crown />
      </span>

      <Tooltip title={getTooltip(customerNames)} placement="top">
        <Typography className="content">{customerNames}</Typography>
      </Tooltip>
    </>
  )
}

function ProjectLink({ link }: { link: NonNullable<CardViewModel["projectLink"]> }) {
  const { createPathWithContext } = useUrlWithContext()

  const path = createPathWithContext(link.href, { withSubContext: true })

  return (
    <>
      <span className={"iconWrapper"}>
        <Folder />
      </span>

      <Tooltip title={getTooltip(link.label)} placement="top">
        <MuiLink component={Link} to={path} className="content">
          {link.label}
        </MuiLink>
      </Tooltip>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    padding: theme.spacing(0.5, 0.5, 0.5, 1),
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 16,
  },

  rowContent: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    maxWidth: "calc(100% - 32px)",
    flexGrow: 1,

    "& .iconWrapper": {
      display: "flex",
      alignItems: "center",
      color: theme.palette.grey[500],
      marginTop: -4,
      "& svg": {
        width: 12,
        height: 12,
      },
    },

    "& .content": {
      color: theme.palette.grey[600],
      fontSize: 10,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },

  rowAction: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(),
  },
}))
